import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ImageUpload from '../../components/ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyMember = {
  name: '', position_de: '', position_en: '', position_es: '',
  image: '', email: '', order: 0, active: true,
};

export default function AdminTeam() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyMember });
  const token = localStorage.getItem('itcs-admin-token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => axios.get(`${API}/admin/team`, { headers }).then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...emptyMember }); setEditing(null); setDialogOpen(true); };
  const openEdit = (item) => { setForm({ ...item }); setEditing(item.id); setDialogOpen(true); };
  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/admin/team/${editing}`, form, { headers });
        toast.success('Teammitglied aktualisiert');
      } else {
        await axios.post(`${API}/admin/team`, form, { headers });
        toast.success('Teammitglied erstellt');
      }
      setDialogOpen(false); load();
    } catch { toast.error('Fehler beim Speichern'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Teammitglied wirklich löschen?')) return;
    try { await axios.delete(`${API}/admin/team/${id}`, { headers }); toast.success('Gelöscht'); load(); }
    catch { toast.error('Fehler'); }
  };

  return (
    <div data-testid="admin-team">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-[#0F172A]">Team verwalten</h1>
        <Button onClick={openNew} className="bg-[#066ce7] hover:bg-[#005bb5] text-white" data-testid="add-team-btn"><Plus className="w-4 h-4 mr-2" /> Neues Mitglied</Button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Bild</TableHead><TableHead>Name</TableHead><TableHead>Position (DE)</TableHead><TableHead>E-Mail</TableHead><TableHead>Aktiv</TableHead><TableHead className="text-right">Aktionen</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map(m => (
              <TableRow key={m.id}>
                <TableCell><img src={m.image || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100'} alt={m.name} className="w-10 h-10 rounded-full object-cover" /></TableCell>
                <TableCell className="font-medium">{m.name}</TableCell>
                <TableCell className="text-sm text-slate-500">{m.position_de}</TableCell>
                <TableCell className="text-sm text-slate-500">{m.email}</TableCell>
                <TableCell>{m.active ? <Badge className="bg-emerald-100 text-emerald-700">Aktiv</Badge> : <Badge variant="outline">Inaktiv</Badge>}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(m)}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(m.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Mitglied bearbeiten' : 'Neues Mitglied'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => updateForm('name', e.target.value)} /></div>
              <div><Label className="text-xs">E-Mail</Label><Input value={form.email} onChange={e => updateForm('email', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Position DE</Label><Input value={form.position_de} onChange={e => updateForm('position_de', e.target.value)} /></div>
              <div><Label className="text-xs">Position EN</Label><Input value={form.position_en} onChange={e => updateForm('position_en', e.target.value)} /></div>
              <div><Label className="text-xs">Position ES</Label><Input value={form.position_es} onChange={e => updateForm('position_es', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Reihenfolge</Label><Input type="number" value={form.order} onChange={e => updateForm('order', parseInt(e.target.value) || 0)} /></div>
              <div className="flex items-center gap-3 pt-5"><Switch checked={form.active} onCheckedChange={v => updateForm('active', v)} /><Label className="text-xs">Aktiv</Label></div>
            </div>
            <ImageUpload value={form.image} onChange={v => updateForm('image', v)} label="Profilbild" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} className="bg-[#066ce7] text-white" data-testid="save-team-btn">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
